import axios from "axios";

// Prefer the current page's origin so the session cookie stays FIRST-PARTY on
// every domain the deployment is linked to (vslex.in, court-drafting-hub.emergent.host,
// any future custom domain). Falls back to REACT_APP_BACKEND_URL for SSR/build-time
// scenarios where `window` is unavailable.
//
// Why this matters: mobile browsers (iOS Safari ITP, Samsung Internet, Brave, FF on
// iOS) block third-party cookies. If the page is on vslex.in but POST /api/auth/session
// goes to court-drafting-hub.emergent.host, the Set-Cookie response is treated as
// third-party and silently dropped — sign-in fails. Using the page's origin keeps the
// API call same-origin → first-party cookies → mobile sign-in works.
const ORIGIN =
    (typeof window !== "undefined" && window.location && window.location.origin)
        ? window.location.origin
        : process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${ORIGIN}/api`;

export const api = axios.create({
    baseURL: API_BASE,
    withCredentials: true,
    timeout: 180000,
});

api.interceptors.response.use(
    (r) => r,
    (err) => {
        // Budget-exhausted (402) → broadcast a global event so any page can show the banner
        if (err?.response?.status === 402) {
            const detail = err?.response?.data?.detail || "AI credit balance exhausted.";
            try {
                window.dispatchEvent(new CustomEvent("counseliq:budget-exhausted", { detail }));
            } catch {}
        }
        return Promise.reject(err);
    },
);

export const authApi = {
    me: () => api.get("/auth/me").then((r) => r.data),
    createSession: (session_id) => api.post("/auth/session", { session_id }).then((r) => r.data),
    logout: () => api.post("/auth/logout").then((r) => r.data),
    updatePrefs: (preferences) => api.put("/auth/preferences", { preferences }).then((r) => r.data),
};

export const mattersApi = {
    list: () => api.get("/matters").then((r) => r.data),
    get: (id) => api.get(`/matters/${id}`).then((r) => r.data),
    create: (data) => api.post("/matters", data).then((r) => r.data),
    update: (id, data) => api.put(`/matters/${id}`, data).then((r) => r.data),
    remove: (id) => api.delete(`/matters/${id}`).then((r) => r.data),
    dashboard: () => api.get("/matters/_stats/dashboard").then((r) => r.data),
};

export const clientsApi = {
    list: (q, archived = false) => api.get("/clients", { params: { q, archived } }).then((r) => r.data),
    get: (id) => api.get(`/clients/${id}`).then((r) => r.data),
    create: (data) => api.post("/clients", data).then((r) => r.data),
    update: (id, data) => api.put(`/clients/${id}`, data).then((r) => r.data),
    remove: (id) => api.delete(`/clients/${id}`).then((r) => r.data),
    conflictCheck: (payload) => api.post("/clients/conflict_check", payload).then((r) => r.data),
    addContact: (clientId, payload) => api.post(`/clients/${clientId}/contacts`, payload).then((r) => r.data),
    removeContact: (clientId, contactId) => api.delete(`/clients/${clientId}/contacts/${contactId}`).then((r) => r.data),
    linkKyc: (clientId, payload) => api.post(`/clients/${clientId}/kyc/link`, payload).then((r) => r.data),
};

export const tasksApi = {
    list: (params) => api.get("/tasks", { params }).then((r) => r.data),
    create: (data) => api.post("/tasks", data).then((r) => r.data),
    update: (id, data) => api.put(`/tasks/${id}`, data).then((r) => r.data),
    remove: (id) => api.delete(`/tasks/${id}`).then((r) => r.data),
    dashboard: () => api.get("/tasks/_stats/dashboard").then((r) => r.data),
};

export const workflowApi = {
    stageTemplates: () => api.get("/workflow/stage_templates").then((r) => r.data),
    setStage: (payload) => api.post("/workflow/stage", payload).then((r) => r.data),
    limitationCauses: () => api.get("/workflow/limitation_causes").then((r) => r.data),
    computeLimitation: (payload) => api.post("/workflow/limitation", payload).then((r) => r.data),
    saveLimitation: (payload) => api.post("/workflow/limitation/save", payload).then((r) => r.data),
    checklists: () => api.get("/workflow/checklists").then((r) => r.data),
    assignChecklist: (payload) => api.post("/workflow/checklists/assign", payload).then((r) => r.data),
    toggleChecklistItem: (payload) => api.post("/workflow/checklists/toggle", payload).then((r) => r.data),
    engagementLetterInstructions: (clientId) => api.get(`/workflow/engagement_letter/instructions/${clientId}`).then((r) => r.data),
};

export const hearingsApi = {
    list: (params) => api.get("/hearings", { params }).then((r) => r.data),
    create: (data) => api.post("/hearings", data).then((r) => r.data),
    update: (id, data) => api.put(`/hearings/${id}`, data).then((r) => r.data),
    remove: (id) => api.delete(`/hearings/${id}`).then((r) => r.data),
    detectConflicts: (payload) => api.post("/hearings/conflicts", payload).then((r) => r.data),
    today: () => api.get("/hearings/_stats/today").then((r) => r.data),
};

export const causeListsApi = {
    list: (court) => api.get("/cause_lists", { params: court ? { court } : {} }).then((r) => r.data),
    get: (id) => api.get(`/cause_lists/${id}`).then((r) => r.data),
    providers: () => api.get("/cause_lists/providers").then((r) => r.data),
    fetch: (payload) => api.post("/cause_lists/fetch", payload).then((r) => r.data),
    upload: ({ file, court, list_date }) => {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("court", court);
        fd.append("list_date", list_date);
        return api.post("/cause_lists/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
    },
    remove: (id) => api.delete(`/cause_lists/${id}`).then((r) => r.data),
    manualMatch: (runId, itemId, payload) => api.post(`/cause_lists/${runId}/items/${itemId}/match`, payload).then((r) => r.data),
    createHearing: (runId, itemId) => api.post(`/cause_lists/${runId}/items/${itemId}/create_hearing`).then((r) => r.data),
};

export const courtSearchApi = {
    providers: () => api.get("/court_search/providers").then((r) => r.data),
    search: (payload) => api.post("/court_search", payload).then((r) => r.data),
    history: () => api.get("/court_search/history").then((r) => r.data),
    fetchDoc: (tid) => api.get(`/court_search/doc/${tid}`).then((r) => r.data),
};

export const utilitiesApi = {
    courtFeeCategories: () => api.get("/utilities/court_fee/categories").then((r) => r.data),
    computeCourtFee: (payload) => api.post("/utilities/court_fee", payload).then((r) => r.data),
    causeTitle: (payload) => api.post("/utilities/cause_title", payload).then((r) => r.data),
    hearingPrep: (payload) => api.post("/utilities/hearing_prep", payload).then((r) => r.data),
};

export const billingApi = {
    // dashboard
    dashboard: () => api.get("/billing/_stats/dashboard").then((r) => r.data),
    outstanding: () => api.get("/billing/_stats/outstanding").then((r) => r.data),
    // time
    listTime: (params) => api.get("/billing/time", { params }).then((r) => r.data),
    runningTimer: () => api.get("/billing/time/_running").then((r) => r.data),
    startTimer: (matter_id, description) => api.post("/billing/time/start", null, { params: { matter_id, description } }).then((r) => r.data),
    stopTimer: (entry_id) => api.post("/billing/time/stop", null, { params: { entry_id } }).then((r) => r.data),
    createTime: (data) => api.post("/billing/time", data).then((r) => r.data),
    updateTime: (id, data) => api.put(`/billing/time/${id}`, data).then((r) => r.data),
    removeTime: (id) => api.delete(`/billing/time/${id}`).then((r) => r.data),
    // expenses
    listExpenses: (params) => api.get("/billing/expenses", { params }).then((r) => r.data),
    expenseCategories: () => api.get("/billing/expenses/categories").then((r) => r.data),
    createExpense: (data) => api.post("/billing/expenses", data).then((r) => r.data),
    updateExpense: (id, data) => api.put(`/billing/expenses/${id}`, data).then((r) => r.data),
    removeExpense: (id) => api.delete(`/billing/expenses/${id}`).then((r) => r.data),
    // agreements
    listAgreements: (params) => api.get("/billing/agreements", { params }).then((r) => r.data),
    createAgreement: (data) => api.post("/billing/agreements", data).then((r) => r.data),
    updateAgreement: (id, data) => api.put(`/billing/agreements/${id}`, data).then((r) => r.data),
    removeAgreement: (id) => api.delete(`/billing/agreements/${id}`).then((r) => r.data),
    // invoices
    listInvoices: (params) => api.get("/billing/invoices", { params }).then((r) => r.data),
    getInvoice: (id) => api.get(`/billing/invoices/${id}`).then((r) => r.data),
    generateInvoice: (data) => api.post("/billing/invoices/generate", data).then((r) => r.data),
    updateInvoice: (id, data) => api.put(`/billing/invoices/${id}`, data).then((r) => r.data),
    removeInvoice: (id) => api.delete(`/billing/invoices/${id}`).then((r) => r.data),
    invoicePdfUrl: (id) => `${API_BASE}/billing/invoices/${id}/pdf`,
    recordPayment: (id, payload) => api.post(`/billing/invoices/${id}/payments`, payload).then((r) => r.data),
    razorpayOrder: (id) => api.post(`/billing/invoices/${id}/razorpay/order`).then((r) => r.data),
};

export const docsApi = {
    list: (matter_id) => api.get("/documents", { params: { matter_id } }).then((r) => r.data),
    get: (id) => api.get(`/documents/${id}`).then((r) => r.data),
    upload: (file, matter_id) => {
        const fd = new FormData();
        fd.append("file", file);
        if (matter_id) fd.append("matter_id", matter_id);
        return api.post("/documents/upload", fd).then((r) => r.data);
    },
    downloadUrl: (id) => `${API_BASE}/documents/${id}/download`,
    ask: (document_id, query) => api.post("/documents/ask", { document_id, query }).then((r) => r.data),
    chronology: (document_ids, language = "en") =>
        api.post("/documents/chronology", { document_ids, language }).then((r) => r.data),
    crossQuery: (payload) => api.post("/documents/cross_query", payload).then((r) => r.data),
    translate: (text, target) => api.post("/documents/translate", { text, target }).then((r) => r.data),
    update: (id, payload) => api.patch(`/documents/${id}`, payload).then((r) => r.data),
    remove: (id) => api.delete(`/documents/${id}`).then((r) => r.data),
};

export const researchApi = {
    status: () => api.get("/research/status").then((r) => r.data),
    ask: (payload) => api.post("/research/ask", payload).then((r) => r.data),
    memos: (matter_id) => api.get("/research/memos", { params: { matter_id } }).then((r) => r.data),
    memo: (id) => api.get(`/research/memos/${id}`).then((r) => r.data),
    remove: (id) => api.delete(`/research/memos/${id}`).then((r) => r.data),
};

export const draftsApi = {
    types: () => api.get("/drafts/types").then((r) => r.data),
    list: (matter_id) => api.get("/drafts", { params: { matter_id } }).then((r) => r.data),
    get: (id) => api.get(`/drafts/${id}`).then((r) => r.data),
    generate: (payload) => api.post("/drafts/generate", payload).then((r) => r.data),
    refine: (id, payload) => api.post(`/drafts/${id}/refine`, payload).then((r) => r.data),
    jobStatus: (job_id) => api.get(`/drafts/jobs/${job_id}`).then((r) => r.data),
    update: (id, payload) => api.put(`/drafts/${id}`, payload).then((r) => r.data),
    remove: (id) => api.delete(`/drafts/${id}`).then((r) => r.data),
    redline: (id, a, b) => api.get(`/drafts/${id}/redline`, { params: { a, b } }).then((r) => r.data),
    revert: (id, version) => api.post(`/drafts/${id}/revert/${version}`).then((r) => r.data),
};

export const voiceApi = {
    transcribe: (blob, language) => {
        const fd = new FormData();
        fd.append("file", blob, "voice.webm");
        if (language) fd.append("language", language);
        return api.post("/voice/transcribe", fd).then((r) => r.data);
    },
    speak: (text, voice = "alloy") =>
        api.post("/voice/speak", { text, voice }, { responseType: "blob" }).then((r) => r.data),
};

export const vaultApi = {
    list: (kind, tag) => api.get("/vault", { params: { kind, tag } }).then((r) => r.data),
    create: (data) => api.post("/vault", data).then((r) => r.data),
    get: (id) => api.get(`/vault/${id}`).then((r) => r.data),
    update: (id, data) => api.put(`/vault/${id}`, data).then((r) => r.data),
    remove: (id) => api.delete(`/vault/${id}`).then((r) => r.data),
};

export const exportsApi = {
    draftDocx: (id) => `${API_BASE}/export/draft/${id}/docx`,
    draftPdf: (id) => `${API_BASE}/export/draft/${id}/pdf`,
    memoDocx: (id) => `${API_BASE}/export/memo/${id}/docx`,
    memoPdf: (id) => `${API_BASE}/export/memo/${id}/pdf`,
};

export const chambersApi = {
    list: () => api.get("/chambers").then((r) => r.data),
    pendingInvites: () => api.get("/chambers/invites/pending").then((r) => r.data),
    create: (data) => api.post("/chambers", data).then((r) => r.data),
    get: (id) => api.get(`/chambers/${id}`).then((r) => r.data),
    update: (id, data) => api.put(`/chambers/${id}`, data).then((r) => r.data),
    remove: (id) => api.delete(`/chambers/${id}`).then((r) => r.data),
    invite: (id, email, role) => api.post(`/chambers/${id}/invite`, { email, role }).then((r) => r.data),
    accept: (invite_id) => api.post(`/chambers/invites/${invite_id}/accept`).then((r) => r.data),
    changeRole: (chamber_id, member_id, role) =>
        api.put(`/chambers/${chamber_id}/members/${member_id}/role`, { role }).then((r) => r.data),
    removeMember: (chamber_id, member_id) =>
        api.delete(`/chambers/${chamber_id}/members/${member_id}`).then((r) => r.data),
    leave: (id) => api.post(`/chambers/${id}/leave`).then((r) => r.data),
};

export const adminApi = {
    stats: () => api.get("/admin/stats").then((r) => r.data),
    users: () => api.get("/admin/users").then((r) => r.data),
    chambers: () => api.get("/admin/chambers").then((r) => r.data),
    audit: (limit = 200) => api.get("/admin/audit", { params: { limit } }).then((r) => r.data),
    setRole: (user_id, role) => api.put(`/admin/users/${user_id}/role`, null, { params: { role } }).then((r) => r.data),
    listAllowlist: () => api.get("/admin/allowlist").then((r) => r.data),
    addAllowlist: (email, note) => api.post("/admin/allowlist", { email, note }).then((r) => r.data),
    removeAllowlist: (id) => api.delete(`/admin/allowlist/${id}`).then((r) => r.data),
};

export const superadminApi = {
    users: (q) => api.get("/superadmin/users", { params: { q } }).then((r) => r.data),
    overview: (uid) => api.get(`/superadmin/users/${uid}/overview`).then((r) => r.data),
    research: (uid, limit = 100) => api.get(`/superadmin/users/${uid}/research`, { params: { limit } }).then((r) => r.data),
    interactions: (uid, limit = 100) => api.get(`/superadmin/users/${uid}/interactions`, { params: { limit } }).then((r) => r.data),
    activity: (uid) => api.get(`/superadmin/users/${uid}/activity`).then((r) => r.data),
    impersonate: (uid) => api.post(`/superadmin/impersonate/${uid}`).then((r) => r.data),
    stopImpersonate: () => api.post("/superadmin/impersonate/stop").then((r) => r.data),
    chamberSetup: (uid, payload) => api.post(`/superadmin/users/${uid}/chamber_setup`, payload).then((r) => r.data),
};

export const adminUsageApi = {
    summary: (period = "week", scope = "all") => api.get("/admin/usage/llm/summary", { params: { period, scope } }).then((r) => r.data),
    daily: (days = 30, scope = "all") => api.get("/admin/usage/llm/daily", { params: { days, scope } }).then((r) => r.data),
    recent: (params = {}) => api.get("/admin/usage/llm/recent", { params }).then((r) => r.data),
    pricing: () => api.get("/admin/usage/llm/pricing").then((r) => r.data),
};

export const strategyApi = {
    // Case strength assessment
    assess: (matter_id, payload) => api.post(`/strategy/assess/${matter_id}`, payload).then((r) => r.data),
    listAssessments: (matter_id) => api.get(`/strategy/assessments/${matter_id}`).then((r) => r.data),
    removeAssessment: (id) => api.delete(`/strategy/assessments/${id}`).then((r) => r.data),
    // Risks
    listRisks: (matter_id) => api.get("/strategy/risks", { params: { matter_id } }).then((r) => r.data),
    createRisk: (data) => api.post("/strategy/risks", data).then((r) => r.data),
    updateRisk: (id, data) => api.put(`/strategy/risks/${id}`, data).then((r) => r.data),
    removeRisk: (id) => api.delete(`/strategy/risks/${id}`).then((r) => r.data),
    // Arguments
    listArguments: (matter_id) => api.get("/strategy/arguments", { params: { matter_id } }).then((r) => r.data),
    createArgument: (data) => api.post("/strategy/arguments", data).then((r) => r.data),
    updateArgument: (id, data) => api.put(`/strategy/arguments/${id}`, data).then((r) => r.data),
    removeArgument: (id) => api.delete(`/strategy/arguments/${id}`).then((r) => r.data),
    suggestArguments: (data) => api.post("/strategy/arguments/suggest", data).then((r) => r.data),
    // Evidence
    listEvidence: (matter_id) => api.get("/strategy/evidence", { params: { matter_id } }).then((r) => r.data),
    createEvidence: (data) => api.post("/strategy/evidence", data).then((r) => r.data),
    updateEvidence: (id, data) => api.put(`/strategy/evidence/${id}`, data).then((r) => r.data),
    removeEvidence: (id) => api.delete(`/strategy/evidence/${id}`).then((r) => r.data),
    // Witnesses
    listWitnesses: (matter_id) => api.get("/strategy/witnesses", { params: { matter_id } }).then((r) => r.data),
    createWitness: (data) => api.post("/strategy/witnesses", data).then((r) => r.data),
    updateWitness: (id, data) => api.put(`/strategy/witnesses/${id}`, data).then((r) => r.data),
    removeWitness: (id) => api.delete(`/strategy/witnesses/${id}`).then((r) => r.data),
    witnessPrep: (id) => api.post(`/strategy/witnesses/${id}/prep`).then((r) => r.data),
    // Appellate
    listAppellate: (matter_id) => api.get("/strategy/appellate", { params: { matter_id } }).then((r) => r.data),
    createAppellate: (data) => api.post("/strategy/appellate", data).then((r) => r.data),
    updateAppellate: (id, data) => api.put(`/strategy/appellate/${id}`, data).then((r) => r.data),
    removeAppellate: (id) => api.delete(`/strategy/appellate/${id}`).then((r) => r.data),
    draftGrounds: (data) => api.post("/strategy/appellate/draft_grounds", data).then((r) => r.data),
};

export const clausesApi = {
    categories: () => api.get("/clauses/categories").then((r) => r.data),
    list: (params) => api.get("/clauses", { params }).then((r) => r.data),
    get: (id) => api.get(`/clauses/${id}`).then((r) => r.data),
    create: (data) => api.post("/clauses", data).then((r) => r.data),
    update: (id, data) => api.put(`/clauses/${id}`, data).then((r) => r.data),
    remove: (id) => api.delete(`/clauses/${id}`).then((r) => r.data),
    use: (id) => api.post(`/clauses/${id}/use`).then((r) => r.data),
};

export const pleadingCheckApi = {
    consistency: (data) => api.post("/pleading_check/consistency", data).then((r) => r.data),
    contractRedflag: (data) => api.post("/pleading_check/contract_redflag", data).then((r) => r.data),
    listReports: (params) => api.get("/pleading_check/reports", { params }).then((r) => r.data),
    getReport: (id) => api.get(`/pleading_check/reports/${id}`).then((r) => r.data),
    removeReport: (id) => api.delete(`/pleading_check/reports/${id}`).then((r) => r.data),
};

export const analyticsApi = {
    practice: () => api.get("/analytics/practice").then((r) => r.data),
    outcomes: (group_by = "court") => api.get("/analytics/outcomes", { params: { group_by } }).then((r) => r.data),
    financial: (period = "fy") => api.get("/analytics/financial", { params: { period } }).then((r) => r.data),
    productivity: (days = 30) => api.get("/analytics/productivity", { params: { days } }).then((r) => r.data),
};

export const precedentsApi = {
    list: (params) => api.get("/precedents", { params }).then((r) => r.data),
    get: (id) => api.get(`/precedents/${id}`).then((r) => r.data),
    create: (data) => api.post("/precedents", data).then((r) => r.data),
    update: (id, data) => api.put(`/precedents/${id}`, data).then((r) => r.data),
    remove: (id) => api.delete(`/precedents/${id}`).then((r) => r.data),
    use: (id, matter_id) => api.post(`/precedents/${id}/use`, null, { params: matter_id ? { matter_id } : {} }).then((r) => r.data),
    statsByCourt: () => api.get("/precedents/_stats/by_court").then((r) => r.data),
};

export const playbooksApi = {
    listSystem: () => api.get("/playbooks/system").then((r) => r.data),
    getSystem: (key) => api.get(`/playbooks/system/${key}`).then((r) => r.data),
    list: () => api.get("/playbooks").then((r) => r.data),
    create: (data) => api.post("/playbooks", data).then((r) => r.data),
    update: (id, data) => api.put(`/playbooks/${id}`, data).then((r) => r.data),
    remove: (id) => api.delete(`/playbooks/${id}`).then((r) => r.data),
    apply: (data) => api.post("/playbooks/apply", data).then((r) => r.data),
};

export const complianceApi = {
    bciRules: () => api.get("/compliance/bci_rules").then((r) => r.data),
    selfAudit: (data) => api.post("/compliance/self_audit", data).then((r) => r.data),
    conflictScan: (data) => api.post("/compliance/conflict_scan", data).then((r) => r.data),
    feedSources: () => api.get("/compliance/regulatory_feed/sources").then((r) => r.data),
    addFeedSource: (data) => api.post("/compliance/regulatory_feed/sources", data).then((r) => r.data),
    removeFeedSource: (id) => api.delete(`/compliance/regulatory_feed/sources/${id}`).then((r) => r.data),
    feed: (source) => api.get("/compliance/regulatory_feed", { params: { source } }).then((r) => r.data),
};export const docStatus = (doc_id) => api.get(`/documents/${doc_id}/status`).then((r) => r.data);
export const docResummarize = (doc_id) => api.post(`/documents/${doc_id}/summarize`).then((r) => r.data);
export const docReprocess = (doc_id) => api.post(`/documents/${doc_id}/reprocess`).then((r) => r.data);

export { API_BASE };
